import { useQuery } from "@tanstack/react-query";
import { BrandRail } from "../components/BrandRail";
import { FreshnessBar } from "../components/FreshnessBar";
import { ActionsPanel } from "../components/ActionsPanel";
import { queryKeys } from "../lib/query-client";
import type { DataSnapshot } from "../lib/types";

interface GuideApp {
  routes: Array<{ hash: string; name: string; description: string }>;
  keyboard: Array<{ keys: string; action: string }>;
  urlParams: Array<{ param: string; example: string; description: string }>;
  shareExample: string;
  actions: Array<{
    id: string;
    label: string;
    workflow: string;
    mode?: string;
    duration: string;
    description: string;
    confirm?: string;
  }>;
  api: Array<{ path: string; description: string }>;
  export: { formats: string[]; scope: string };
  filters: { presets: string[]; facets: string; kpi: string };
  compare: string;
  dataFlow: string;
  secrets: string;
}

interface GuideContent {
  title: string;
  structural: { max: number; dimensions: Array<{ key: string; label: string; hint: string }> };
  llm: { max: number; dimensions: Array<{ key: string; label: string }>; note: string };
  combined: { formula: string; tiers: Array<{ tier: string; min: number }> };
  status: Array<{ status: string; rule: string }>;
  history: { autoresearch: string; snapshots: string };
  improve: string[];
  app?: GuideApp;
}

interface GuideData {
  guide: GuideContent;
}

interface Props {
  data: DataSnapshot;
}

async function loadGuide(): Promise<GuideContent> {
  const res = await fetch("/data/guide.json", { cache: "no-cache" });
  if (!res.ok) throw new Error("guide.json missing — run npm run build:data");
  const j = (await res.json()) as GuideData;
  return j.guide;
}

export function Guide({ data }: Props) {
  const { data: guide, isLoading, error } = useQuery({
    queryKey: queryKeys.guide,
    queryFn: loadGuide,
  });

  const issueCount = (data.stats.statusCounts?.warning ?? 0) + (data.stats.statusCounts?.critical ?? 0);
  const app = guide?.app;
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}${app?.shareExample ?? "?skill=SKILL_NAME#"}`
      : "";

  return (
    <div className="grid min-h-screen guide-layout" style={{ gridTemplateColumns: "220px 1fr" }}>
      <BrandRail counts={{ monitor: data.stats.total, issues: issueCount }} />
      <main className="min-w-0">
        <FreshnessBar meta={data.meta} />
        <header className="px-6 pt-6 pb-4 border-b border-fg">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              // Reference · {data.stats.total} skills
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              Interactive dashboard
            </span>
          </div>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <h1 className="font-display font-extrabold leading-[0.86] tracking-[-0.045em] text-t-72">
              {guide?.title ?? "Scoring guide"}
            </h1>
            <div className="flex flex-col gap-1 pb-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted">
                How scoring works
              </span>
              <span className="font-mono text-[12px] uppercase tracking-[0.12em] text-muted">
                4 dimensions · {guide?.structural.max ?? 50} structural + {guide?.llm.max ?? 30} llm
              </span>
            </div>
          </div>
          <div className="editorial-rule mt-4" />
        </header>

        {isLoading && (
          <p className="p-6 font-mono text-[11px] uppercase text-muted">// loading guide…</p>
        )}
        {error && (
          <p className="p-6 font-mono text-[11px] uppercase text-bad">// {String(error)}</p>
        )}

        {guide && (
          <div className="p-6 max-w-4xl space-y-10 pb-16">
            {app && (
              <>
                <Section title="// Run actions from the dashboard">
                  <p className="text-[14px] text-muted mb-4 leading-relaxed">
                    Dispatch GitHub Actions on <code className="text-accent">bc-scan-arm</code> (llama.cpp). Requires{" "}
                    <code className="text-accent">GITHUB_TOKEN</code> on Cloudflare Pages.
                  </p>
                  <ActionsPanel showAdvanced />
                  <div className="mt-6 border border-fg">
                    <table className="w-full border-collapse text-[13px]">
                      <thead>
                        <tr className="border-b-2 border-fg">
                          <Th>Action</Th>
                          <Th>Workflow</Th>
                          <Th>Duration</Th>
                          <Th>What it does</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {app.actions.map((a) => (
                          <tr key={a.id} className="border-b border-rule">
                            <Td mono accent>{a.label}</Td>
                            <Td mono>{a.workflow}{a.mode ? ` · ${a.mode}` : ""}</Td>
                            <Td mono muted>{a.duration}</Td>
                            <Td>{a.description}{a.confirm ? " (confirmation required)" : ""}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>

                <Section title="// Routes">
                  <ul className="space-y-3">
                    {app.routes.map((r) => (
                      <li key={r.hash} className="border border-fg p-4">
                        <a href={r.hash} className="font-display font-bold text-t-20 hover:underline">
                          {r.name}
                        </a>
                        <code className="block font-mono text-[10px] uppercase text-muted mt-1">{r.hash}</code>
                        <p className="text-[14px] mt-2 text-muted leading-relaxed">{r.description}</p>
                      </li>
                    ))}
                  </ul>
                </Section>

                <Section title="// Keyboard shortcuts (Monitor)">
                  <table className="w-full border-collapse border border-fg text-[13px]">
                    <tbody>
                      {app.keyboard.map((k) => (
                        <tr key={k.keys} className="border-b border-rule last:border-b-0">
                          <Td mono accent className="w-28">{k.keys}</Td>
                          <Td>{k.action}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>

                <Section title="// URL parameters (shareable links)">
                  <p className="text-[14px] text-muted mb-3">
                    Query string before the hash, e.g.{" "}
                    <code className="text-accent break-all">{shareUrl}</code>
                  </p>
                  <table className="w-full border-collapse border border-fg text-[13px]">
                    <thead>
                      <tr className="border-b-2 border-fg">
                        <Th>Param</Th>
                        <Th>Example</Th>
                        <Th>Description</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {app.urlParams.map((u) => (
                        <tr key={u.param} className="border-b border-rule last:border-b-0">
                          <Td mono accent>{u.param}</Td>
                          <Td mono muted>{u.example}</Td>
                          <Td>{u.description}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>

                <Section title="// Compare & export">
                  <p className="text-[14px] border-b border-rule py-3">{app.compare}</p>
                  <p className="text-[14px] border-b border-rule py-3">
                    <span className="font-mono text-[11px] uppercase mr-2">Export</span>
                    {app.export.formats.join(" · ")} — {app.export.scope}
                  </p>
                  <p className="text-[14px] py-3">
                    <span className="font-mono text-[11px] uppercase mr-2">Filters</span>
                    Presets: {app.filters.presets.join(", ")}. {app.filters.facets} {app.filters.kpi}
                  </p>
                </Section>

                <Section title="// Live API (Cloudflare Pages Functions)">
                  <table className="w-full border-collapse border border-fg text-[13px]">
                    <tbody>
                      {app.api.map((e) => (
                        <tr key={e.path} className="border-b border-rule last:border-b-0">
                          <Td mono accent className="whitespace-nowrap">{e.path}</Td>
                          <Td>{e.description}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>

                <Section title="// Data pipeline">
                  <p className="text-[14px] font-mono leading-relaxed">{app.dataFlow}</p>
                  <p className="text-[14px] text-muted mt-3">{app.secrets}</p>
                </Section>
              </>
            )}

            <Section title="// Structural (0–50)">
              <ul className="space-y-2">
                {guide.structural.dimensions.map((d) => (
                  <li key={d.key} className="border-b border-rule py-2 text-[14px]">
                    <strong className="font-mono text-[11px] uppercase">{d.label}</strong>
                    <span className="block text-muted mt-1">{d.hint}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title={`// LLM (0–${guide.llm.max})`}>
              <p className="text-[14px] text-muted mb-3">{guide.llm.note}</p>
              <ul className="flex flex-wrap gap-4 font-mono text-[11px] uppercase">
                {guide.llm.dimensions.map((d) => (
                  <li key={d.key} className="border border-fg px-3 py-2">{d.label}</li>
                ))}
              </ul>
            </Section>

            <Section title="// Combined score">
              <p className="text-[14px] mb-4">{guide.combined.formula}</p>
              <div className="grid gap-2 grid-cols-2 md:grid-cols-4">
                {guide.combined.tiers.map((t) => (
                  <div key={t.tier} className="border border-fg p-3 font-mono text-[11px] uppercase">
                    <div className="font-display text-t-26 tabular-nums">{t.min}+</div>
                    {t.tier}
                  </div>
                ))}
              </div>
            </Section>

            <Section title="// Status">
              {guide.status.map((s) => (
                <p key={s.status} className="text-[14px] border-b border-rule py-2">
                  <span className="font-mono text-[11px] uppercase mr-2">{s.status}</span>
                  {s.rule}
                </p>
              ))}
            </Section>

            <Section title="// History sources">
              <p className="text-[14px] border-b border-rule py-2">{guide.history.autoresearch}</p>
              <p className="text-[14px] border-b border-rule py-2">{guide.history.snapshots}</p>
            </Section>

            <Section title="// Improve checklist">
              <ol className="list-decimal list-inside space-y-2 text-[14px]">
                {guide.improve.map((step, i) => (
                  <li key={i} className="py-1">{step}</li>
                ))}
              </ol>
            </Section>
          </div>
        )}
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-4 font-medium">{title}</h2>
      {children}
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.14em] text-muted font-medium">
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
  accent,
  muted,
  className = "",
}: {
  children: React.ReactNode;
  mono?: boolean;
  accent?: boolean;
  muted?: boolean;
  className?: string;
}) {
  return (
    <td
      className={`p-3 ${mono ? "font-mono text-[12px]" : ""} ${accent ? "text-accent" : ""} ${muted ? "text-muted" : ""} ${className}`}
    >
      {children}
    </td>
  );
}